import { useMemo, useState } from "react";
import PageTitle from "~/common/components/PageTitle/v1/PageTitle";
import { Link, LinkType } from "~/common/components/Link";
import { putCompleteRegistration } from "~/features/registration/api/putCompleteAccount";
import ExpiredLinkPage from "~/pages/ExpiredLinkPage/ExpiredLinkPage";
import { MarketingConsent } from "~/features/marketingConsent/components/MarketingConsent";
import { redirectToUrl } from "~/utils/url/urlUtils";
import { CompleteRegistrationForm } from "~/features/registration/components/CompleteRegistrationForm";
import { useForm } from "react-hook-form";
import { useOnMount } from "~/common/hooks/useOnMount";
import { HOMEPAGE } from "~/utils/url/staticUrls";
import { getAffiliateDataFromRegistrationLink } from "~/domains/affiliate/utils/affiliateHelper";
import { type AffilateForm, type AFFILIATE_FORM_KEY } from "~/features/affiliate/components/AffiliateFormSection";
import { type AffiliatePartnerData } from "~/domains/affiliate/types/models";
import { transformSalesPartnerDTO } from "~/domains/affiliate/utils/utils";
import { useTranslation } from "react-i18next";
import { isMarketingConsentEnabled } from "~/utils/features";
import { useMyAccountDropdownValues } from "~/features/registration/hooks/useMyAccountDropdownValues";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { ModalConfirmation } from "~/features/myAccount/profile/components/ModalConfirmation";
import { Button, styled } from "~/common/mui";
import { useConfirmBeforeUnload } from "~/features/myAccount/profile/formUtils";

export interface Props {
  titles: { code: string; name: string }[];
  salutations: { code: string; name: string }[];
  industrySectors: { code: string; name: string }[];
  jobTitles: { code: string; name: string }[];
  functionalAreas: { code: string; name: string }[];
  countries: { isocode: string; name: string }[];
  customerCategories: { code: string; name: string }[];
  knowledgeAreas: { code: string; name: string }[];
  languages: { code: string; name: string }[];
  email: string;
  passwordToken: string;
  validLink: boolean;
  salesPartner?: AffiliatePartnerData;
  customer?: PrefilledCustomerData;
  newsletterOne: boolean;
}

interface PrefilledCustomerData {
  firstName: string;
  lastName: string;
  companyName: string;
  salutationCode: string;

  line1: string;
  line2: string;
  zipCode: string;
  city: string;
  country: string;
  phone: string;
}

function adjustPrefilledCustomerData(customer: PrefilledCustomerData): Partial<CompleteRegistrationFormValues> {
  return {
    salutation: customer.salutationCode,
    firstName: customer.firstName,
    lastName: customer.lastName,
    company: customer.companyName,
    address: customer.line1,
    addressAdditional: customer.line2,
    zipCode: customer.zipCode,
    city: customer.city,
    country: customer.country,
    phone: customer.phone,
  };
}

export interface CompleteRegistrationFormValues {
  title: string;
  salutation: string;
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
  company: string;
  industrySector: string;
  jobTitle: string;
  functionalArea: string;
  address: string;
  addressAdditional: string;
  zipCode: string;
  city: string;
  country: string;
  phone: string;
  language: string;
  knowledgeArea: string;
  customerCategory: string;
  vat: string;
  termsTraining: boolean;
  [AFFILIATE_FORM_KEY]?: AffilateForm;
}

export const CompleteRegistrationPage = (data: Props) => {
  const { t } = useTranslation();
  const [isValidLink, setIsValidLink] = useState<boolean>(data.validLink);
  const [globalErrors, setGlobalErrors] = useState<string[]>([]);
  const [newsletterOne, setNewsletterOne] = useState<boolean>(data.newsletterOne);
  const [isEnableFieldsTraining] = useState<boolean>(true);
  const { val: modalCategoryOpen, setTrue: openModalCategory, setFalse: closeModalCategory } = useBooleanState(false);
  const hideMarketingConsent = data.newsletterOne;
  const initialCustomerCategory = data.customerCategories?.length === 1 ? data.customerCategories[0].code : undefined;

  const dropdownOptions = useMyAccountDropdownValues(data);

  const { affiliateId, affiliateAccepted, selectedSubsidiary } = useMemo(getAffiliateDataFromRegistrationLink, []);

  const formProps = useForm<CompleteRegistrationFormValues>({
    defaultValues: {
      email: data.email,
      affiliate: {
        id: affiliateAccepted ? affiliateId ?? "" : "",
        subsidiary: selectedSubsidiary ?? "",
      },
      ...(data.customer ? adjustPrefilledCustomerData(data.customer) : {}),
    },
  });

  useOnMount(() => {
    formProps.register("email");
    formProps.register("affiliate.id");
  });

  const customerCategoryCode = formProps.watch("customerCategory");
  const customerCategoryLabel = data.customerCategories.find((item) => item.code === customerCategoryCode)?.name ?? "";

  const onSubmitHandler = async (values: CompleteRegistrationFormValues, confirmed: boolean) => {
    if (!!customerCategoryCode && !confirmed && isEnableFieldsTraining && !initialCustomerCategory) {
      return openModalCategory();
    } else {
      closeModalCategory();
    }

    const response = await putCompleteRegistration({
      ...values,
      passwordToken: data.passwordToken,
      newsletterOne: newsletterOne,
      affiliateId: affiliateId ?? undefined,
      isEnableFieldsTraining: isEnableFieldsTraining,
    });

    if (Object.keys(response.validationResults).length > 0) {
      Object.entries(response.validationResults).forEach(([fieldName, errs], index) => {
        if (!errs || errs.length === 0) {
          return;
        }

        formProps.setError(
          fieldName as keyof CompleteRegistrationFormValues,
          {
            type: errs[0],
            message: t(errs[0]),
          },
          { shouldFocus: index === 0 },
        );
      });
    } else if (response.globalErrors.length > 0) {
      setIsValidLink(false);
      setGlobalErrors(response.globalErrors);
    } else {
      formProps.reset(formProps.getValues());
      redirectToUrl(HOMEPAGE);
    }
  };

  useConfirmBeforeUnload(formProps.formState);

  return !isValidLink ? (
    <ExpiredLinkPage errors={globalErrors} />
  ) : (
    <>
      <PageTitle title={t("page.completeRegistration.title")} />
      <CompleteRegistrationRoot>
        <form onSubmit={formProps.handleSubmit((data) => onSubmitHandler(data, false))}>
          <CompleteRegistrationForm
            {...formProps}
            email={data.email}
            initialCustomerCategory={initialCustomerCategory}
            {...dropdownOptions}
            salesPartner={data.salesPartner}
            isEnableFieldsTraining={isEnableFieldsTraining}
          />

          {isMarketingConsentEnabled() && !hideMarketingConsent && (
            <MarketingConsent
              className={"scha__complete-registration__marketing-consent"}
              selected={newsletterOne}
              setSelected={(newVal: boolean) => setNewsletterOne(newVal)}
            />
          )}

          {/* BUTTONS*/}
          <ButtonsArea className="d-flex flex-justify-end">
            <Link
              variant={LinkType.PRIMARY}
              href={"#"}
              className="scha__complete-registration__form__buttons-area__cancel"
              data-test-id={"test-id"}
            >
              <span className="pl-3 pr-3">{t("page.completeRegistration.cancel")}</span>
            </Link>
            <Button data-test-id={"test-id-0"} onClick={formProps.handleSubmit((data) => onSubmitHandler(data, false))}>
              {t("text.company.save.button")}
            </Button>
          </ButtonsArea>
        </form>

        {modalCategoryOpen && (
          <ModalConfirmation
            onClose={closeModalCategory}
            onSubmit={formProps.handleSubmit((data) => onSubmitHandler(data, true))}
            selectedValue={customerCategoryLabel}
            inputField={t(`page.completeRegistration.customerCategory`)}
          />
        )}
      </CompleteRegistrationRoot>
    </>
  );
};

const CompleteRegistrationRoot = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(4),
  "& .scha__complete-registration__marketing-consent": {
    padding: theme.spacing(1.5),
    [theme.breakpoints.up("tablet")]: {
      padding: theme.spacing(3.75),
    },
  },
}));

const ButtonsArea = styled("div")(({ theme }) => ({
  width: "100%",
  marginTop: theme.spacing(4),
  display: "flex",
  justifyContent: "flex-end",
  "& .scha__complete-registration__form__buttons-area__cancel": {
    padding: `0 ${theme.spacing(3.75)}`,
    marginRight: theme.spacing(4),
  },
}));

export interface CreateProps {
  titles: { code: string; name: string }[];
  salutations: { code: string; name: string }[];
  industrySectors: { code: string; name: string }[];
  jobTitles: { code: string; name: string }[];
  functionalAreas: { code: string; name: string }[];
  countries: { isocode: string; name: string }[];
  customerCategories: { code: string; name: string }[];
  knowledgeAreas: { code: string; name: string }[];
  languages: { code: string; name: string }[];
  email: string;
  passwordToken: string;
  validLink: boolean;
  salesPartner?: SalesPartnerDTO;
  newsletterOne: boolean;
}

export function createCompleteRegistrationContainer(data: CreateProps) {
  return (
    <CompleteRegistrationPage
      {...data}
      salesPartner={data.salesPartner ? transformSalesPartnerDTO(data.salesPartner) : undefined}
    />
  );
}
